import ContentRoutes from 'ContentUtils/Routes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import BlogRssFeedDataSelection from 'email-management-lib/EmailCreation/components/BlogRssFeedDataSelection/BlogRssFeedDataSelection';
import EmailOptionSelect from 'email-management-lib/EmailCreation/components/EmailOptionSelection/EmailOptionSelect';
import useBlogRSSFeedDataState, { resetBlogRSSFeedDataState } from 'email-management-lib/EmailCreation/hooks/useBlogRssFeedDataState';
import useEmailSelectedOptionState, { resetEmailSelectedOptionState } from 'email-management-lib/EmailCreation/hooks/useEmailSelectedOptionState';
import useGetEditorHref from 'email-management-lib/EmailCreation/hooks/useGetEditorHref';
import useGetHasExistingBlogEmail from 'email-management-lib/EmailCreation/hooks/useGetHasExistingBlogEmail';
import useTracking from 'email-management-lib/Shared/tracking/useTracking';
import { openURL } from 'email-management-lib/Shared/utils/navigation';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { Wizard, WizardFooter, WizardHeader, WizardStep } from 'foundations-components/transitional/components/Wizard';
import useGetCurrentBusinessUnit, { ALL_BU_SELECTED, DEFAULT_BU_ID } from 'messaging-management-lib/portal/hooks/useGetCurrentBusinessUnit';
import { useCallback, useEffect, useMemo } from 'react';
import CreateEmailPanelDoneButton from './CreateEmailPanelDoneButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function CreateEmailPanel({
  onClose,
  autoStartOnboarding,
  selectedTeamIds,
  onClickCreateEmail,
  tracker
}) {
  const {
    selectedOption
  } = useEmailSelectedOptionState();
  const {
    blogID,
    frequency,
    rssFeedUrl
  } = useBlogRSSFeedDataState();
  const {
    hasExistingEmailForBlog,
    existingBlogEmailID
  } = useGetHasExistingBlogEmail({
    id: blogID,
    frequency
  });
  const emailEditorHref = useGetEditorHref({
    autoStartOnboarding
  });
  const businessUnitID = useGetCurrentBusinessUnit();
  const {
    trackDashboardInteraction
  } = useTracking({
    tracker
  });
  const businessUnitIDToUse = useMemo(() => {
    if (businessUnitID === null) {
      return null;
    }
    return businessUnitID === ALL_BU_SELECTED ? DEFAULT_BU_ID : businessUnitID;
  }, [businessUnitID]);
  const navigateTo = useCallback(({
    href,
    params = {}
  }) => {
    let base = href.startsWith('/') ? href : `/${href}`;
    if (params && Object.keys(params).length > 0) {
      base = href.includes('?') ? `${href}&` : `${href}?`;
    }
    const paramString = Object.keys(params).map(key => {
      if (key === 'teamIds' && Array.isArray(params[key])) {
        return params[key].map(id => `${key}=${id}`).join('&');
      }
      return `${key}=${params[key]}`;
    }).join('&');
    openURL({
      url: `${base}${paramString}`
    });
  }, []);
  const createEmailViaTemplatePicker = useCallback(() => {
    const urlParams = {
      emailType: selectedOption,
      subcategory: 'drag_drop_email'
    };
    if ((selectedTeamIds === null || selectedTeamIds === void 0 ? void 0 : selectedTeamIds.length) > 0) {
      urlParams.teamIds = selectedTeamIds;
    }
    if (businessUnitIDToUse !== null) {
      urlParams.businessUnitId = businessUnitIDToUse;
    }
    if (rssFeedUrl) {
      trackDashboardInteraction({
        otherActions: `selected-create-with-email-type-rss`
      });
      navigateTo({
        href: emailEditorHref,
        params: Object.assign({}, urlParams, {
          rssUrl: encodeURIComponent(rssFeedUrl)
        })
      });
      return;
    }
    if (blogID && frequency) {
      if (!hasExistingEmailForBlog) {
        trackDashboardInteraction({
          otherActions: `selected-create-with-email-type-blog`
        });
        navigateTo({
          href: emailEditorHref,
          params: Object.assign({}, urlParams, {
            blogId: blogID,
            blogEmailType: frequency
          })
        });
      } else if (existingBlogEmailID) {
        trackDashboardInteraction({
          otherActions: `selected-edit-with-email-type-blog`
        });
        navigateTo({
          href: ContentRoutes.edit(existingBlogEmailID, 2)
        });
      }
      return;
    }
    trackDashboardInteraction({
      otherActions: `selected-create-with-email-type-${selectedOption}`
    });
    navigateTo({
      href: emailEditorHref,
      params: urlParams
    });
  }, [selectedOption, selectedTeamIds, businessUnitIDToUse, rssFeedUrl, blogID, frequency, trackDashboardInteraction, navigateTo, emailEditorHref, hasExistingEmailForBlog, existingBlogEmailID]);
  const onClickNext = useCallback(() => {
    if (onClickCreateEmail && !hasExistingEmailForBlog) {
      const args = {
        selectedEmailType: selectedOption,
        rssFeedUrl,
        selectedBlog: blogID,
        selectedFrequency: frequency
      };
      if (businessUnitIDToUse !== null) args.businessUnitId = businessUnitIDToUse;
      onClickCreateEmail(args);
    } else {
      createEmailViaTemplatePicker();
    }
  }, [onClickCreateEmail, hasExistingEmailForBlog, selectedOption, rssFeedUrl, blogID, frequency, businessUnitIDToUse, createEmailViaTemplatePicker]);
  useEffect(() => {
    return () => {
      resetEmailSelectedOptionState();
      resetBlogRSSFeedDataState();
    };
  }, []);
  return /*#__PURE__*/_jsx(ModalPanel, {
    width: 450,
    "data-test-id": "create-email-panel",
    children: /*#__PURE__*/_jsxs(Wizard, {
      defaultHeading: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "email-management-creation.createEmailPanel.title"
      }),
      headerComponent: WizardHeader,
      footerComponent: WizardFooter,
      cancellable: true,
      backable: true,
      onReject: onClose,
      onConfirm: onClickNext,
      defaultDoneButton: CreateEmailPanelDoneButton,
      children: [/*#__PURE__*/_jsx(WizardStep, {
        name: "Select Email Type",
        children: /*#__PURE__*/_jsx(EmailOptionSelect, {})
      }), selectedOption === 'BLOG_RSS' && /*#__PURE__*/_jsx(WizardStep, {
        name: "Blog/RSS",
        children: /*#__PURE__*/_jsx(BlogRssFeedDataSelection, {})
      })]
    })
  });
}